import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, MediaPreviewDialog } from '@xxtract/ui';

import { formatDate, formatFileSize } from '@/lib/formatters';

/** Pictogramnaam, geen tekst voor de gebruiker. */
const SPINNER_ICON = 'progress_activity';
import type { MediaLibraryItem } from '../media.api';

/** Scheidingsteken tussen gegevens — geen tekst voor de gebruiker. */
const SCHEIDING = '\u00b7';

/** typeInfo/displayType values with no previewable image (SDS = PDF, download-link only). */
export const DOCUMENT_DISPLAY_TYPES = new Set(['sds']);

const TYPE_ICONS: Record<MediaLibraryItem['displayType'], string> = {
  packshot: 'image',
  etiket: 'label',
  sds: 'description',
  marketing: 'photo_library',
  overig: 'draft',
};

export interface MediaCardProps {
  item: MediaLibraryItem;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onDownload: (item: MediaLibraryItem) => void;
  /** Wordt dit bestand op dit moment opgehaald? Per bestand, zodat niet elke tegel bezig lijkt. */
  isDownloading?: boolean;
  /** Haalt het adres van de grote versie op — pas wanneer het venster opengaat. */
  loadWebImage?: (item: MediaLibraryItem) => Promise<string | null>;
  /** Navigates to the product-detail page for this item's product link (FR52, h11-4). Optional: h11-1 has no product-detail route wired for the library yet. */
  productHref?: string;
}

/**
 * One tile in the DAM library grid (h11-1, mockup dam.html "mcard"): selection checkbox,
 * type badge, thumbnail/placeholder, file + product meta, and a per-file download action.
 * Selection is presentational infrastructure for h11-2's bulkbar; downloading a single file
 * reuses the h2-4 per-file download route via the `onDownload` callback (authenticated blob
 * fetch, never a plain href — the download route is Bearer-protected).
 */
export function MediaCard({ item, selected, onToggleSelect, onDownload, productHref, isDownloading = false, loadWebImage }: MediaCardProps) {
  const { t } = useTranslation();
  const isDocument = DOCUMENT_DISPLAY_TYPES.has(item.displayType);
  const previewUrl = !isDocument ? item.thumbnailUrl : null;
  const sizeLabel = item.sizeBytes != null ? formatFileSize(item.sizeBytes) : null;
  // Dezelfde grond als de server hanteert voor het filter "niet gekoppeld": geen productverwijzing.
  // Tot 22-08-2026 keek het scherm naar een leeg productnummer, waardoor een bestand dat wél een
  // productnummer heeft maar bij geen bekend product hoort, hier als gekoppeld werd getoond — met
  // een lege merknaam en een lege productnaam in beeld.
  const unlinked = item.productId == null;

  // FR56: the product reference always shows brand + name + GTIN, never just the name.
  const productLabel = unlinked
    ? t('media.library.card.unlinked')
    : t('media.library.card.productLine', { brand: item.brand, productName: item.productName, gtin: item.gtin });

  return (
    <div className="relative flex flex-col gap-2 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background)] p-3" data-testid="media-card">
      <div className="flex items-start justify-between gap-2">
        <button
          aria-label={t(selected ? 'media.library.card.deselect' : 'media.library.card.select', { fileName: item.fileName })}
          aria-pressed={selected}
          className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-control)] border border-[var(--border)] text-[var(--foreground-muted)] data-[selected=true]:border-[var(--secondary)] data-[selected=true]:bg-[var(--secondary)] data-[selected=true]:text-white"
          data-selected={selected}
          onClick={() => onToggleSelect(item.id)}
          type="button"
        >
          {selected && <span aria-hidden="true" className="material-symbols-outlined text-[14px]">check</span>}
        </button>
        <Badge variant="outline">{t(`media.library.types.${item.displayType}`)}</Badge>
      </div>

      {/* Een document heeft geen webversie en geen miniatuur; vergroten heeft daar geen betekenis,
          dus blijft het bij Download. */}
      {isDocument || loadWebImage == null ? (
        <div className="flex h-24 items-center justify-center overflow-hidden rounded-[var(--radius-control)] bg-[var(--background-muted)]">
          {previewUrl != null
            ? <img alt={t('media.library.card.thumbnailAlt', { fileName: item.fileName })} className="h-full w-full object-contain" src={previewUrl} />
            : <span aria-hidden="true" className="material-symbols-outlined text-[28px] text-[var(--foreground-muted)]">{TYPE_ICONS[item.displayType]}</span>}
        </div>
      ) : (
        <MediaPreviewDialog
          details={<PreviewDetails item={item} productLabel={productLabel} />}
          labels={{
            title: item.fileName,
            imageAlt: t('media.library.card.thumbnailAlt', { fileName: item.fileName }),
            placeholder: t('media.gallery.placeholder'),
            loading: t('media.library.card.downloading'),
            close: t('common.close'),
            download: t('media.library.card.download'),
          }}
          loadImageSrc={() => loadWebImage(item)}
          onDownload={() => onDownload(item)}
          trigger={(
            <button
              aria-label={t('media.gallery.enlarge', { fileName: item.fileName })}
              className="flex h-24 w-full items-center justify-center overflow-hidden rounded-[var(--radius-control)] bg-[var(--background-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
              type="button"
            >
              {previewUrl != null
                ? <img alt={t('media.library.card.thumbnailAlt', { fileName: item.fileName })} className="h-full w-full object-contain" src={previewUrl} />
                : <span aria-hidden="true" className="material-symbols-outlined text-[28px] text-[var(--foreground-muted)]">{TYPE_ICONS[item.displayType]}</span>}
            </button>
          )}
        />
      )}

      <div>
        <p className="truncate text-sm font-semibold text-[var(--foreground)]" title={item.fileName}>{item.fileName}</p>
        {unlinked ? (
          <p className="truncate text-xs text-[var(--foreground-muted)]">{productLabel}</p>
        ) : productHref != null ? (
          <Link className="block truncate text-xs text-[var(--secondary)] underline-offset-2 hover:underline" to={productHref}>{productLabel}</Link>
        ) : (
          <p className="truncate text-xs text-[var(--foreground-muted)]">{productLabel}</p>
        )}
        {sizeLabel != null && <p className="text-xs text-[var(--foreground-muted)]">{sizeLabel}</p>}
      </div>

      <button
        className="mt-auto flex items-center gap-1 self-start text-xs font-medium text-[var(--secondary)] hover:underline disabled:opacity-60"
        disabled={isDownloading}
        onClick={() => onDownload(item)}
        type="button"
      >
        {isDownloading ? (
          <>
            <span aria-hidden="true" className="material-symbols-outlined animate-spin text-[15px]" data-testid={`media-card-downloading-${item.id}`}>{SPINNER_ICON}</span>
            {t('media.library.card.downloading')}
          </>
        ) : (
          <>
            <span aria-hidden="true" className="material-symbols-outlined text-[15px]">download</span>
            {t('media.library.card.download')}
          </>
        )}
      </button>
    </div>
  );
}

/** De gegevens naast het beeld: product, type en datum. Afmetingen, formaat en grootte levert de
 *  mediaserver niet, dus die staan er bewust niet bij. */
function PreviewDetails({ item, productLabel }: { item: MediaLibraryItem; productLabel: string }) {
  const { t } = useTranslation();
  const datum = item.uploadDate == null ? null : new Date(item.uploadDate);
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span>{productLabel}</span>
      <span aria-hidden="true">{SCHEIDING}</span>
      <span>{t(`media.library.types.${item.displayType}`)}</span>
      {datum != null && !Number.isNaN(datum.getTime()) && (
        <>
          <span aria-hidden="true">{SCHEIDING}</span>
          <span>{formatDate(datum)}</span>
        </>
      )}
    </span>
  );
}
